// Value mappers
const mapYesNo = (v) => (v === "yes" ? "1" : v === "no" ? "2" : "");
const mapSHG = (v) => (v === "shgs" ? "1" : v === "others" ? "2" : "");
const mapRating = (v) =>
    v === "good" ? "1" : v === "satisfactory" ? "2" : v === "poor" ? "3" : "";
const mapPlates = (v) =>
({
    thermocol_plates: "1",
    aluminium_foil_plates: "2",
    other_material: "3",
    not_applicable: "4",
}[v] || "");

// Serialise the payload for console logging — File objects show as "[File: name]"
const serialiseForLog = (payload) =>
    Object.fromEntries(
        Object.entries(payload).map(([k, v]) => [
            k,
            v instanceof File ? `[File: ${v.name}]` : v,
        ])
    );

const checkStatus = (...fields) =>
    fields.every(f => f !== "" && f !== null && f !== undefined && (!Array.isArray(f) || f.length > 0))
        ? "complete"
        : "drift";

export const KIOSK_SECTION_FIELDS = {
    a: [
        "gen_info_A1",
        "gen_info_A1_remark",
        "gen_info_A1_upload",
        "gen_info_A2",
        "gen_info_A2_remark",
        "gen_info_A3",
        "gen_info_A4",
        "gen_info_A4_to",
        "gen_info_A5",
        "gen_info_A6",
        "is_gen_info_survey_done",
    ],

    b: [
        "quali_quanti_asmnt_B1",
        "quali_quanti_asmnt_B2",
        "quali_quanti_asmnt_B3",
        "is_quali_and_quanti_assessment_survey_done",
    ],

    c: [
        "water_safety_C1",
        "water_safety_C2",
        "water_safety_C2_photo",
        "water_safety_C3",
        "water_safety_C3_date",
        "water_safety_C3_upload",
        "is_water_safety_survey_done",
    ],

    d: [
        "gen_clean_D1",
        "gen_clean_D2",
        "gen_clean_D2_remark",
        "gen_clean_D3",
        "gen_clean_D3_upload",
        "gen_clean_D4",
        "is_gen_clean_survey_done",
    ],

    e: [
        "safety_arngmnt_E1",
        "safety_arngmnt_E2",
        "is_safety_arngmnt_survey_done",
    ],

    f: [
        "sanitation_F1",
        "sanitation_F2",
        "sanitation_F3",
        "sanitation_F3_upload",
        "sanitation_F4",
        "sanitation_F4_upload",
        "is_sanitation_survey_done",
    ],

    g: [
        "wst_mngmnt_G1",
        "wst_mngmnt_G2",
        "wst_mngmnt_G2_upload",
        "wst_mngmnt_G3",
        "wst_mngmnt_G3_upload",
        "wst_mngmnt_G4",
        "is_wst_mngmnt_survey_done",
    ],

    h: [
        "hygiene_H1",
        "hygiene_H1_upload",
        "hygiene_H2",
        "hygiene_H2_upload",
        "hygiene_H3",
        "hygiene_H4",
        "hygiene_H5",
        "hygiene_H5_upload",
        "hygiene_H6",
        "is_hygiene_survey_done",
    ],

    i: [
        "food_hygine_I1",
        "food_hygine_remarks",
        "is_food_hygine_survey_done",
    ],

    j: [
        "iec_monitoring_J1",
        "iec_monitoring_J2",
        "iec_monitoring_J3",
        "is_iec_monitoring_survey_done",
    ],

    k: [
        "challenges",
        "suggestions",
    ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Common fields for all section saves
// ─────────────────────────────────────────────────────────────────────────────

export const KIOSK_COMMON_FIELDS = [
    "reporting_date",
    "kiosk_location_fulladdress",
    "lattitude_longitude",
    "district_id",
    "municipality_id",
    "ward_id",
    "canteens_id",
    "canteen_type_id",
];

/**
 * Builds the API-compatible flat payload for the kiosk survey.
 * Sections C–J prefixes are inferred from the naming pattern used in sections A & B
 * (confirmed: gen_info_A*, quali_quanti_asmnt_B*).  Confirm the rest with the backend.
 */
export const buildKioskApiPayload = ({
    answers,
    canteen,
    locationState, // { districtId, municipalityId, wardId }
    userLocation,
    reportingDate,
    address,
}) => {
    const a = answers;

    const payload = {
        // ── Meta ──────────────────────────────────────────────────────────────────
        reporting_date: reportingDate,
        kiosk_location_fulladdress: address || canteen?.kitchen_address || "",
        lattitude_longitude: userLocation
            ? `${userLocation.latitude},${userLocation.longitude}`
            : "",
        district_id: locationState?.districtId || "",
        municipality_id: locationState?.municipalityId || "",
        ward_id: locationState?.wardId || "",
        canteens_id: canteen?.canteen_type || "",
        canteen_type_id: canteen?.id || "",

        gen_info_A1: mapYesNo(a.A1),
        gen_info_A1_remark: a.A1_remark || "",
        gen_info_A1_upload: a.A1_file || null,
        gen_info_A2: mapSHG(a.A2),
        gen_info_A2_remarks: a.A2_remark || "",
        gen_info_A3: a.A3 || "",
        gen_info_A4: a.A4_from || "",        // time "from"
        gen_info_A4_to: a.A4_to || "",      // time "to"
        gen_info_A5: a.A5 || "",
        gen_info_A6: mapYesNo(a.A6),
        is_gen_info_survey_done: checkStatus(a.A1, a.A2, a.A3, a.A4_from, a.A4_to, a.A5, a.A6),

        quali_quanti_asmnt_B1: mapRating(a.B1),
        quali_quanti_asmnt_B2: mapRating(a.B2),
        quali_quanti_asmnt_B3: mapYesNo(a.B3),
        is_quali_and_quanti_assessment_survey_done: checkStatus(a.B1, a.B2, a.B3),

        water_safety_C1: mapYesNo(a.C1),
        water_safety_C2: mapYesNo(a.C2),
        water_safety_C2_photo: a.C2_photo || null,
        water_safety_C3: mapYesNo(a.C3),
        water_safety_C3_date: a.C3_date || "",
        water_safety_C3_upload: a.C3_file || null,
        is_water_safety_survey_done: checkStatus(a.C1, a.C2, a.C3),

        gen_clean_D1: mapYesNo(a["D.1"]),
        gen_clean_D2: Array.isArray(a["D.2"]) ? a["D.2"].join(",") : "",
        gen_clean_D2_remark: a["D.2_remark"] || "",
        gen_clean_D3: mapYesNo(a["D.3"]),
        gen_clean_D3_upload: a["D.3_file"] || null,
        gen_clean_D4: mapRating(a["D.4"]),
        is_gen_clean_survey_done: checkStatus(a["D.1"], a["D.2"], a["D.3"], a["D.4"]),

        safety_arngmnt_E1: mapYesNo(a["E.1"]),
        safety_arngmnt_E2: mapYesNo(a["E.2"]),
        is_safety_arngmnt_survey_done: checkStatus(a["E.1"], a["E.2"]),

        sanitation_F1: mapYesNo(a["F.1"]),
        sanitation_F2: mapYesNo(a["F.2"]),
        sanitation_F3: mapYesNo(a["F.3"]),
        sanitation_F3_upload: a["F.3_file"] || null,
        sanitation_F4: mapYesNo(a["F.4"]),
        sanitation_F4_upload: a["F.4_photo"] || null,
        is_sanitation_survey_done: checkStatus(a["F.1"], a["F.2"], a["F.3"], a["F.4"]),

        wst_mngmnt_G1: mapYesNo(a["G.1"]),
        wst_mngmnt_G2: mapYesNo(a["G.2"]),
        wst_mngmnt_G2_upload: a["G.2_photo"] || null,
        wst_mngmnt_G3: mapYesNo(a["G.3"]),
        wst_mngmnt_G3_upload: a["G.3_file"] || null,
        wst_mngmnt_G4: mapYesNo(a["G.4"]),
        is_wst_mngmnt_survey_done: checkStatus(a["G.1"], a["G.2"], a["G.3"], a["G.4"]),

        hygiene_H1: mapYesNo(a["H.1"]),
        hygiene_H1_upload: a["H.1_photo"] || null,
        hygiene_H2: mapYesNo(a["H.2"]),
        hygiene_H2_upload: a["H.2_photo"] || null,
        hygiene_H3: mapYesNo(a["H.3"]),
        hygiene_H4: mapYesNo(a["H.4"]),
        hygiene_H5: mapYesNo(a["H.5"]),
        hygiene_H5_upload: a["H.5_file"] || null,
        hygiene_H6: mapYesNo(a["H.6"]),
        is_hygiene_survey_done: checkStatus(a["H.1"], a["H.2"], a["H.3"], a["H.4"], a["H.5"], a["H.6"]),

        food_hygine_I1: mapPlates(a["I.1"]),
        food_hygine_remarks: a["I.1_remark"] || "",
        is_food_hygine_survey_done: checkStatus(a["I.1"]),

        iec_monitoring_J1: mapYesNo(a["J.1"]),
        iec_monitoring_J2: mapYesNo(a["J.2"]),
        iec_monitoring_J3: mapYesNo(a["J.3"]),
        is_iec_monitoring_survey_done: checkStatus(a["J.1"], a["J.2"], a["J.3"]),

        challenges: a["1"] || "",
        suggestions: a["2"] || "",

        check_survey_completed: null,
        is_total_survey_complete: null
    };


    return { payload, logPayload: serialiseForLog(payload) };
};

export const parseKioskApiResponse = (apiData) => {
    // Helper to extract values
    const getVal = (key) => apiData[key] !== undefined && apiData[key] !== null ? String(apiData[key]) : "";

    // Reverse mappers
    const rYesNo = (v) => v === "1" ? "yes" : v === "2" ? "no" : "";
    const rSHG = (v) => v === "1" ? "shgs" : v === "2" ? "others" : "";
    const rRating = (v) => v === "1" ? "good" : v === "2" ? "satisfactory" : v === "3" ? "poor" : "";
    const rPlates = (v) => v === "1" ? "thermocol_plates" : v === "2" ? "aluminium_foil_plates" : v === "3" ? "other_material" : v === "4" ? "not_applicable" : "";

    const a = {};

    // Section A
    a.A1 = rYesNo(getVal("gen_info_A1"));
    a.A1_remark = getVal("gen_info_A1_remark");
    a.A2 = rSHG(getVal("gen_info_A2"));
    a.A2_remark = getVal("gen_info_A2_remarks");
    a.A3 = getVal("gen_info_A3");
    a.A4_from = getVal("gen_info_A4");
    a.A4_to = getVal("gen_info_A4_to");
    a.A5 = getVal("gen_info_A5");
    a.A6 = rYesNo(getVal("gen_info_A6"));

    // Section B
    a.B1 = rRating(getVal("quali_quanti_asmnt_B1"));
    a.B2 = rRating(getVal("quali_quanti_asmnt_B2"));
    a.B3 = rYesNo(getVal("quali_quanti_asmnt_B3"));

    // Section C
    a.C1 = rYesNo(getVal("water_safety_C1"));
    a.C2 = rYesNo(getVal("water_safety_C2"));
    a.C3 = rYesNo(getVal("water_safety_C3"));
    a.C3_date = getVal("water_safety_C3_date");

    // Section D
    a["D.1"] = rYesNo(getVal("gen_clean_D1"));
    const d2 = getVal("gen_clean_D2");
    a["D.2"] = d2 ? d2.split(",") : [];
    a["D.2_remark"] = getVal("gen_clean_D2_remark");
    a["D.3"] = rYesNo(getVal("gen_clean_D3"));
    a["D.4"] = rRating(getVal("gen_clean_D4"));

    // Section E
    a["E.1"] = rYesNo(getVal("safety_arngmnt_E1"));
    a["E.2"] = rYesNo(getVal("safety_arngmnt_E2"));

    // Section F
    a["F.1"] = rYesNo(getVal("sanitation_F1"));
    a["F.2"] = rYesNo(getVal("sanitation_F2"));
    a["F.3"] = rYesNo(getVal("sanitation_F3"));
    a["F.4"] = rYesNo(getVal("sanitation_F4"));

    // Section G
    a["G.1"] = rYesNo(getVal("wst_mngmnt_G1"));
    a["G.2"] = rYesNo(getVal("wst_mngmnt_G2"));
    a["G.3"] = rYesNo(getVal("wst_mngmnt_G3"));
    a["G.4"] = rYesNo(getVal("wst_mngmnt_G4"));

    // Section H
    a["H.1"] = rYesNo(getVal("hygiene_H1"));
    a["H.2"] = rYesNo(getVal("hygiene_H2"));
    a["H.3"] = rYesNo(getVal("hygiene_H3"));
    a["H.4"] = rYesNo(getVal("hygiene_H4"));
    a["H.5"] = rYesNo(getVal("hygiene_H5"));
    a["H.5_file"] = getVal("hygiene_H5_upload");
    a["H.6"] = rYesNo(getVal("hygiene_H6"));

    // Section I
    a["I.1"] = rPlates(getVal("food_hygine_I1"));
    a["I.1_remark"] = getVal("food_hygine_remarks");

    // Section J
    a["J.1"] = rYesNo(getVal("iec_monitoring_J1"));
    a["J.2"] = rYesNo(getVal("iec_monitoring_J2"));
    a["J.3"] = rYesNo(getVal("iec_monitoring_J3"));

    // Challenges & Suggestions
    a["1"] = getVal("challenges");
    a["2"] = getVal("suggestions");

    // Completed Sections List
    const completedSections = [];
    if (getVal("is_gen_info_survey_done") === "complete" || checkStatus(a.A1, a.A2, a.A3, a.A4_from, a.A4_to, a.A5, a.A6) === "complete") completedSections.push("a");
    if (getVal("is_quali_and_quanti_assessment_survey_done") === "complete" || checkStatus(a.B1, a.B2, a.B3) === "complete") completedSections.push("b");
    if (getVal("is_water_safety_survey_done") === "complete" || checkStatus(a.C1, a.C2, a.C3) === "complete") completedSections.push("c");
    if (getVal("is_gen_clean_survey_done") === "complete" || checkStatus(a["D.1"], a["D.2"], a["D.3"], a["D.4"]) === "complete") completedSections.push("d");
    if (getVal("is_safety_arngmnt_survey_done") === "complete" || checkStatus(a["E.1"], a["E.2"]) === "complete") completedSections.push("e");
    if (getVal("is_sanitation_survey_done") === "complete" || checkStatus(a["F.1"], a["F.2"], a["F.3"], a["F.4"]) === "complete") completedSections.push("f");
    if (getVal("is_wst_mngmnt_survey_done") === "complete" || checkStatus(a["G.1"], a["G.2"], a["G.3"], a["G.4"]) === "complete") completedSections.push("g");
    if (getVal("is_hygiene_survey_done") === "complete" || checkStatus(a["H.1"], a["H.2"], a["H.3"], a["H.4"], a["H.5"], a["H.6"]) === "complete") completedSections.push("h");
    if (getVal("is_food_hygine_survey_done") === "complete" || checkStatus(a["I.1"]) === "complete") completedSections.push("i");
    if (getVal("is_iec_monitoring_survey_done") === "complete" || checkStatus(a["J.1"], a["J.2"], a["J.3"]) === "complete") completedSections.push("j");
    // challenges and suggestions section k doesn't have a status usually, check if values exist
    if (a["1"] || a["2"]) completedSections.push("k");

    return { answers: a, completedSections };
};